/**
 * DeviceManagement Component - Device status management and control page
 * 
 * Features:
 * - Device search by MAC address functionality
 * - Device status toggle with confirmation dialog
 * - Disabled devices table display
 * - Real-time status updates via API calls
 * - Switch UI for enable/disable device status
 * 
 * @author CTS - Ford PM UI Team
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

// Custom styled components
import {
  DeviceManagementContainer,
  DeviceManagementOuterContainer,
  DeviceManagementPageHeader,
  DeviceManagementPanel,
  DeviceManagementSearchPanel,
  DeviceManagementSearchField,
  DeviceManagementPrimaryButton,
  DeviceManagementSecondaryButton,
  DeviceManagementTableContainer,
  DeviceManagementStatusSwitch,
  DeviceManagementConfirmDialog,
  DeviceManagementDialogTitle,
  DeviceManagementDialogContent,
  DeviceManagementDialogActions,
  DeviceManagementLoadingMessage,
  DeviceManagementErrorMessage,
  DeviceManagementSectionHeader,
  DeviceManagementClickableDeviceName,
  DeviceManagementDeviceMacContainer,
  DeviceManagementCopyIcon,
  DeviceManagementSearchResultsHeader,
  DeviceManagementRefreshButton,
  DeviceManagementSearchFieldContainer,
  DeviceManagementGlobalSearchField,
  DeviceManagementClearSearchIcon,
  DeviceManagementEmptyStateCell,
  DeviceManagementEmptyStateText,
  DeviceManagementPaginationContainer,
  DeviceManagementDialogText,
  DeviceManagementFormControl,
  DeviceManagementActionMenuText,
  DeviceManagementConfirmationText
} from './DeviceManagement.styles';
import { DashboardMonospaceText } from '../dashBoard/Dashboard.styles';
import StatusIndicator from '../../components/StatusIndicator.jsx';

// API functions (to be created)
import {
  searchDeviceApi,
  toggleDeviceStatusApi,
  getDisabledDevicesApi
} from '../../api/deviceManagementApi';
import DeviceDrawer from './DeviceDrawer';

import { AppLabels } from '../../constant/constant';
import { copyToClipboard } from '../../utils/clipboardUtils.jsx';
import { renderTextWithTooltip, renderCriticalDeviceListWithDualSupply, formattedText } from '../../utils/TextTooltipUtils.jsx';

// Reusable table header component
const DeviceTableHeader = () => (
  <TableHead>
    <TableRow>
      {DEVICE_MANAGEMENT_UTILS.getTableHeaders().map((header, index) => (
        <TableCell key={`${header}` + index}>{header}</TableCell>
      ))}
    </TableRow>
  </TableHead>
);

// Common utilities to reduce duplication
const DEVICE_MANAGEMENT_UTILS = {
  // Safe logging utility that only logs in development mode
  safeLog: {
    info: (message, ...args) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[DeviceManagement] ${message}`, ...args);
      }
    },
    error: (message, ...args) => {
      if (process.env.NODE_ENV === 'development') {
        console.error(`[DeviceManagement] ${message}`, ...args);
      }
    },
    warn: (message, ...args) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[DeviceManagement] ${message}`, ...args);
      }
    }
  },

  // Common state reset patterns
  resetDialogState: () => ({
    open: false,
    device: null,
    newStatus: null,
    selectedAction: '',
    availableActions: []
  }),

  // Common chip class utility
  getChipClass: (status = '') => {
    const s = status.toLowerCase();
    const statusMap = {
      'chip-ok': ['ok', 'active', 'connected', 'online', 'ready'],
      'chip-warn': ['warn', 'warning', 'degraded'],
      'chip-error': ['error', 'offline', 'failed', 'down']
    };
    
    for (const [chipType, keywords] of Object.entries(statusMap)) {
      if (keywords.some(k => s.includes(k))) return chipType;
    }
    return 'chip-muted';
  },

  // Common table headers
  getTableHeaders: () => [
    AppLabels.deviceManagementLabels.deviceNameHeader,
    AppLabels.deviceManagementLabels.deviceMacHeader,
    AppLabels.deviceManagementLabels.siteHeader,
    AppLabels.deviceManagementLabels.locationHeader,
    AppLabels.deviceManagementLabels.statusHeader,
    AppLabels.deviceManagementLabels.smartPlugLocationHeader,
    AppLabels.deviceManagementLabels.criticalDevicesHeader,
    AppLabels.deviceManagementLabels.disableEnableHeader
  ]
};

/**
 * DeviceManagement Main Component
 */
export default function DeviceManagement() {
  const { user } = useAuth();

  // Search state
  const [deviceMac, setDeviceMac] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Device data state
  const [deviceData, setDeviceData] = useState(null);
  const [disabledDevices, setDisabledDevices] = useState([]);
  const [error, setError] = useState(null);
  const [disabledDevicesError, setDisabledDevicesError] = useState(null);

  // Pagination state (0-based for MUI TablePagination)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Loading states
  // const [loading, setLoading] = useState(false); // Commented out as not used currently
  const [disabledDevicesLoading, setDisabledDevicesLoading] = useState(false);
  const [statusToggleLoading, setStatusToggleLoading] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    device: null,
    newStatus: null,
    selectedAction: '',
    availableActions: []
  });

  // Device drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Global search state for disabled devices
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');

  // Load disabled devices on component mount
  useEffect(() => {
    loadDisabledDevices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear search results when device MAC input is cleared
  useEffect(() => {
    if (!deviceMac.trim()) {
      setDeviceData(null);
      setSearched(false);
      setError(null);
    }
  }, [deviceMac]);

  /**
   * Load disabled devices from API
   */
  const loadDisabledDevices = async () => {
    setDisabledDevicesLoading(true);
    setDisabledDevicesError(null);
    try {
      const response = await getDisabledDevicesApi();
      const devices = response?.devices || [];
      setDisabledDevices(devices);
      DEVICE_MANAGEMENT_UTILS.safeLog.info('Loaded disabled devices:', devices);
    } catch (error) {
      DEVICE_MANAGEMENT_UTILS.safeLog.error('Error loading disabled devices:', error);
      setDisabledDevicesError('Failed to load disabled devices. Please try again.');
      setDisabledDevices([]);
    } finally {
      setDisabledDevicesLoading(false);
    }
  };

  /**
   * Search for device by MAC address
   */
  const handleSearch = async () => {
    if (!deviceMac.trim()) {
      setError('Please enter a device MAC address');
      return;
    }

    setSearchLoading(true);
    setError(null);
    setDeviceData(null);
    setSearched(false);
    try {
      const response = await searchDeviceApi(deviceMac.trim());
      setDeviceData(response);
      setSearched(true);
      DEVICE_MANAGEMENT_UTILS.safeLog.info('Device search result:', response);
    } catch (error) {
      DEVICE_MANAGEMENT_UTILS.safeLog.error('Device search error:', error);
      setError(error.response?.data?.message || 'Device not found or search failed');
      setSearched(true);
    } finally {
      setSearchLoading(false);
    }
  };

  /**
   * Handle device status toggle confirmation
   */
  const handleStatusToggle = (device, newStatus) => {
    // Load available actions from fallback function
    const fallbackActions = getAvailableActionsFallback(newStatus);

    // Set dialog state with loaded actions
    setConfirmDialog({
      open: true,
      device,
      newStatus,
      selectedAction: '',
      availableActions: fallbackActions
    });
  };

  /**
   * Confirm and execute device status toggle
   */
  const confirmStatusToggle = async () => {
    const { device, newStatus, selectedAction } = confirmDialog;

    // Validate action selection
    if (!selectedAction) {
      setError(AppLabels.deviceManagementLabels.noActionSelected);
      return;
    }

    setStatusToggleLoading(true);

    try {
      // Call API with selected action and user email
      await toggleDeviceStatusApi(device.device_mac, newStatus, selectedAction, user?.email);

      // Update device data if it's the searched device
      if (deviceData && deviceData.device_mac === device.device_mac) {
        setDeviceData(prev => ({
          ...prev,
          is_active: newStatus === 'enabled'
        }));
      }

      // Reload disabled devices to reflect changes
      await loadDisabledDevices();

      DEVICE_MANAGEMENT_UTILS.safeLog.info(`Device ${device.device_mac} action ${selectedAction} executed with status ${newStatus}`);
    } catch (error) {
      DEVICE_MANAGEMENT_UTILS.safeLog.error('Status toggle error:', error);
      setError(error.response?.data?.message || AppLabels.deviceManagementLabels.actionFailed);
    } finally {
      setStatusToggleLoading(false);
      setConfirmDialog(DEVICE_MANAGEMENT_UTILS.resetDialogState());
    }
  };

  /**
   * Cancel status toggle
   */
  const cancelStatusToggle = () => {
    setConfirmDialog(DEVICE_MANAGEMENT_UTILS.resetDialogState());
  };

  /**
   * Get available actions based on device status (fallback for API failure)
   */
  const getAvailableActionsFallback = (status) => {
    const { deviceManagementLabels } = AppLabels;

    if (status === 'enabled') {
      return deviceManagementLabels.actionOptions.enabled;
    } else if (status === 'disabled') {
      return deviceManagementLabels.actionOptions.disabled;
    } else {
      return deviceManagementLabels.actionOptions.default;
    }
  };

  /**
   * Handle device name click to open drawer
   */
  const handleDeviceNameClick = (device) => {
    setSelectedDevice(device);
    setDrawerOpen(true);
  };

  /**
   * Handle drawer close
   */
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedDevice(null);
  };

  /**
   * Handle action selection change
   */
  const handleActionChange = (event) => {
    setConfirmDialog(prev => ({
      ...prev,
      selectedAction: event.target.value
    }));
  };

  /**
   * Handle Enter key down in search field
   */
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };


  /**
   * Render critical devices with dual supply support
   */
  const renderCriticalDevices = (device) => {
    const criticalDevices = device.criticaldevices || [];
    const dualCriticalDevices = device.dualcriticaldevices || [];

    // Create a combined list for display
    const allCriticalIds = [
      ...criticalDevices.map(cd => cd.critical_id || cd.critical_device_name),
      ...dualCriticalDevices.map(dcd => dcd.dual_critical_id || dcd.critical_device_name)
    ].filter(Boolean);

    const criticalDeviceList = allCriticalIds.join(', ') || AppLabels.notApplicable;

    return renderCriticalDeviceListWithDualSupply(
      criticalDeviceList,
      criticalDevices,
      dualCriticalDevices
    );
  };

  /**
   * Handle page change for MUI TablePagination
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Handle rows per page change for MUI TablePagination
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Filter devices based on global search term
   */
  const getFilteredDevices = (devices) => {
    if (!globalSearchTerm.trim()) {
      return devices;
    }

    const searchTerm = globalSearchTerm.toLowerCase();
    return devices.filter(device =>
      device.sp_DeviceName?.toLowerCase().includes(searchTerm) ||
      device.device_mac?.toLowerCase().includes(searchTerm)
    );
  };

  /**
   * Get paginated devices
   */
  const getPaginatedDevices = (devices) => {
    const filteredDevices = getFilteredDevices(devices);
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredDevices.slice(startIndex, endIndex);
  };

  /**
   * Render device table row with Dashboard styling
   */
  const renderDeviceRow = (device, isDisabledTable = false) => {
    const isEnabled = device.is_active === true;

    return (
      <TableRow key={device.device_mac}>
        <TableCell>
          <DeviceManagementClickableDeviceName onClick={() => handleDeviceNameClick(device)}>
            {device.sp_DeviceName.toUpperCase()}
          </DeviceManagementClickableDeviceName>
        </TableCell>
        <TableCell>
          <DeviceManagementDeviceMacContainer>
            <DashboardMonospaceText>
              {device.device_mac}
            </DashboardMonospaceText>
            <Tooltip title={AppLabels.deviceManagementLabels.copyDeviceMacTooltip} arrow>
              <DeviceManagementCopyIcon
                onClick={(event) => copyToClipboard(device.device_mac, event)}
              />
            </Tooltip>
          </DeviceManagementDeviceMacContainer>
        </TableCell>
        <TableCell>{renderTextWithTooltip(device.store_name, 'storeName')}</TableCell>
        <TableCell>{renderTextWithTooltip(device.location_name, 'locationId')}</TableCell>
        <TableCell>
          <StatusIndicator status={device.status} size="medium" />
        </TableCell>
        <TableCell>{formattedText(device.smart_plug_location)}</TableCell>
        <TableCell>{renderCriticalDevices(device)}</TableCell>
        <TableCell>
          <Tooltip title={`${isEnabled ? 'Enable' : 'Disable'} device`}>
            <DeviceManagementStatusSwitch
              checked={isEnabled}
              onChange={(e) => handleStatusToggle(device, e.target.checked ? 'enabled' : 'disabled')}
              disabled={statusToggleLoading}
            />
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <DeviceManagementOuterContainer>
      <DeviceManagementContainer>
        <DeviceManagementPageHeader>
          <Typography variant="h2" component="h2">Device Management</Typography>
          <Typography className="subtitle" component="p">
            Search and manage device status, enable or disable devices as needed
          </Typography>
        </DeviceManagementPageHeader>

        {/* Search Panel */}
        <DeviceManagementSearchPanel>
          <DeviceManagementSearchField
            label="Device MAC ID"
            placeholder="Enter Device MAC ID (e.g., shellyplusplugs-c82e180a3e40)"
           value={deviceMac}
            onChange={(e) => setDeviceMac(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={searchLoading}
            size="small"
          />
          <DeviceManagementPrimaryButton
            onClick={handleSearch}
            disabled={searchLoading || !deviceMac.trim()}
            startIcon={searchLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SearchIcon />
            )}
          >
            {searchLoading ? 'Searching...' : 'Search Device'}
          </DeviceManagementPrimaryButton>
        </DeviceManagementSearchPanel>

        {/* Error Message */}
        {error && (
          <DeviceManagementErrorMessage>
            {error}
          </DeviceManagementErrorMessage>
        )}

        {/* Search Results */}
        {searched && !error && (
          <DeviceManagementPanel>
            <DeviceManagementSearchResultsHeader>
              <Typography variant="h6">{AppLabels.deviceManagementLabels.searchResultsTitle}</Typography>
            </DeviceManagementSearchResultsHeader>

            {deviceData ? (
              <DeviceManagementTableContainer>
                <Table>
                  <DeviceTableHeader />
                  <TableBody>
                    {renderDeviceRow(deviceData)}
                  </TableBody>
                </Table>
              </DeviceManagementTableContainer>
            ) : (
              <DeviceManagementLoadingMessage>
                {AppLabels.deviceManagementLabels.noDeviceFoundWithMac}
              </DeviceManagementLoadingMessage>
            )}
          </DeviceManagementPanel>
        )}

        {/* Disabled Devices Section */}
        {!disabledDevicesError && !disabledDevicesLoading && 
        <>
        <DeviceManagementSectionHeader>
          {AppLabels.deviceManagementLabels.disabledDevicesTitle} ({disabledDevices.length} Devices)
          <Tooltip title={AppLabels.deviceManagementLabels.refreshDisabledDevicesTooltip}>
            <DeviceManagementRefreshButton
              onClick={loadDisabledDevices}
              disabled={disabledDevicesLoading}
            >
              {disabledDevicesLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RefreshIcon />
              )}
            </DeviceManagementRefreshButton>
          </Tooltip>
        </DeviceManagementSectionHeader>

        <DeviceManagementPanel>
          {disabledDevicesError && (
            <DeviceManagementErrorMessage>
              {disabledDevicesError}
            </DeviceManagementErrorMessage>
          )}

          {disabledDevicesLoading && (
            <DeviceManagementLoadingMessage>
              <CircularProgress size={20} />
              Loading disabled devices...
            </DeviceManagementLoadingMessage>
          )}

          {!disabledDevicesLoading && disabledDevices.length > 0 && (
            <>
              {/* Global Search Field */}
              <DeviceManagementSearchFieldContainer>
                <DeviceManagementGlobalSearchField
                  fullWidth
                  variant="outlined"
                  placeholder={AppLabels.deviceManagementLabels.searchPlaceholder}
                  value={globalSearchTerm}
                  onChange={(e) => {
                    setGlobalSearchTerm(e.target.value);
                    setPage(0); // Reset to first page when searching
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: globalSearchTerm && (
                        <InputAdornment position="end">
                          <Tooltip title={AppLabels.deviceManagementLabels.clearSearchTooltip}>
                            <DeviceManagementClearSearchIcon
                              onClick={() => {
                                setGlobalSearchTerm('');
                                setPage(0);
                              }}
                            >
                              <ClearIcon />
                            </DeviceManagementClearSearchIcon>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }
                  }}
                />
              </DeviceManagementSearchFieldContainer>

              <DeviceManagementTableContainer>
                <Table>
                  <DeviceTableHeader />
                  <TableBody>
                    {getPaginatedDevices(disabledDevices).length === 0 ? (
                      <TableRow>
                        <DeviceManagementEmptyStateCell colSpan={7}>
                          <DeviceManagementEmptyStateText variant="body2">
                            {globalSearchTerm.trim()
                              ? `${AppLabels.deviceManagementLabels.noDevicesFound} "${globalSearchTerm}"`
                              : AppLabels.deviceManagementLabels.noDisabledDevicesFound
                            }
                          </DeviceManagementEmptyStateText>
                        </DeviceManagementEmptyStateCell>
                      </TableRow>
                    ) : (
                      getPaginatedDevices(disabledDevices).map(device => renderDeviceRow(device, true))
                    )}
                  </TableBody>
                </Table>
              </DeviceManagementTableContainer>

              {/* MUI Table Pagination */}
              {getFilteredDevices(disabledDevices).length > 10 && (
                <DeviceManagementPaginationContainer
                  component="div"
                  count={getFilteredDevices(disabledDevices).length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              )}
            </>
          )}

          {!disabledDevicesLoading && !disabledDevicesError && disabledDevices.length === 0 && (
            <DeviceManagementLoadingMessage>
              No disabled devices found
            </DeviceManagementLoadingMessage>
          )}
        </DeviceManagementPanel>
        </> }
        

        {/* Confirmation Dialog */}
        <DeviceManagementConfirmDialog
          open={confirmDialog.open}
          onClose={cancelStatusToggle}
          maxWidth="sm"
          fullWidth
        >
          <DeviceManagementDialogTitle>
            <Typography variant="body1">
              {AppLabels.deviceManagementLabels.confirmDialogTitle}
            </Typography>
          </DeviceManagementDialogTitle>
          <DeviceManagementDialogContent>
            <DeviceManagementDialogText>
              {AppLabels.deviceManagementLabels.deviceMacLabel} <strong>{confirmDialog.device?.device_mac}</strong>
            </DeviceManagementDialogText>

            <DeviceManagementFormControl fullWidth>
              <InputLabel id="action-select-label" shrink>
                {AppLabels.deviceManagementLabels.actionSelectionLabel}
              </InputLabel>
              <Select
                labelId="action-select-label"
                value={confirmDialog.selectedAction}
                label={AppLabels.deviceManagementLabels.actionSelectionLabel}
                onChange={handleActionChange}
                size="small"
                displayEmpty
              >
                <MenuItem value="">
                  {AppLabels.deviceManagementLabels.actionSelectionPlaceholder}
                </MenuItem>
                {confirmDialog.availableActions.map((action) => (
                  <MenuItem key={action} value={action}>
                    <DeviceManagementActionMenuText variant="body2">
                      {action}
                    </DeviceManagementActionMenuText>
                  </MenuItem>
                ))}
              </Select>
            </DeviceManagementFormControl>

            {confirmDialog.selectedAction && (
              <DeviceManagementConfirmationText variant="body2">
                {AppLabels.deviceManagementLabels.actionConfirmationMessage}
              </DeviceManagementConfirmationText>
            )}
          </DeviceManagementDialogContent>
          <DeviceManagementDialogActions>
            <DeviceManagementSecondaryButton
              onClick={cancelStatusToggle}
              disabled={statusToggleLoading}
            >
              {AppLabels.deviceManagementLabels.cancelButton}
            </DeviceManagementSecondaryButton>
            <DeviceManagementPrimaryButton
              onClick={confirmStatusToggle}
              disabled={statusToggleLoading || !confirmDialog.selectedAction}
              startIcon={statusToggleLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : null}
            >
              {statusToggleLoading ?
                AppLabels.deviceManagementLabels.processingButton :
                AppLabels.deviceManagementLabels.confirmButton
              }
            </DeviceManagementPrimaryButton>
          </DeviceManagementDialogActions>
        </DeviceManagementConfirmDialog>

        {/* Device Details Drawer */}
        {drawerOpen && selectedDevice && (
          <DeviceDrawer
            device={selectedDevice}
            onClose={handleDrawerClose}
          />
        )}
      </DeviceManagementContainer>
    </DeviceManagementOuterContainer>
  );
}
